import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CrudSection from "@/components/CrudSection";
import { PageHeader, StatusBadge, DueBadge, fmtDate } from "@/components/common";
import DueThisMonth from "@/components/DueThisMonth";
import ContactCell from "@/components/ContactCell";
import NoteCell from "@/components/NoteCell";
import MarkDoneButton from "@/components/MarkDoneButton";
import AttachmentsCell from "@/components/AttachmentsCell";

const BUILDINGS = ["Main", "East", "North", "Polaris", "Main Lot", "Miller Oil"];
const STATUSES = ["not_started", "in_progress", "waiting_repair", "completed"];
const today = new Date().toISOString().slice(0, 10);

const fieldDate = { key: "entered_date", label: "Date Entered", type: "date", default: today, required: true };
const fieldTitle = { key: "title", label: "Task", type: "text", full: true, required: true, placeholder: "Replace air filters" };
const fieldContactStatus = { key: "contact_status", label: "Repair Contact Made?", type: "select", default: "not_contacted", required: true, options: ["not_contacted", "contacted"] };
const fieldContactedWho = { key: "contacted_who", label: "Who Was Contacted", type: "text", placeholder: "e.g. Anderson HVAC" };
const fieldContactedWhen = { key: "contacted_when", label: "Date Contacted", type: "date" };
const fieldBuilding = { key: "building", label: "Building", type: "select", default: "Main", required: true, options: BUILDINGS };
const fieldStatus = { key: "status", label: "Status", type: "select", default: "not_started", required: true, options: STATUSES };
const fieldInitials = { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" };
const fieldNotes = { key: "notes", label: "Notes", type: "textarea", full: true, placeholder: "e.g. Doors were measured; waiting on quote" };

const colDate = { key: "entered_date", label: "Entered", mono: true, render: (r) => r.entered_date ? fmtDate(r.entered_date) : <span className="text-gray-300">—</span> };
const colTask = { key: "title", label: "Task" };
const colBuilding = { key: "building", label: "Building", render: (r) => <span className="font-medium">{r.building || "—"}</span> };
const colContacted = { key: "contact", label: "Contacted", render: (r) => <ContactCell row={r} /> };
const colStatus = { key: "status", label: "Status", editable: true, options: STATUSES, render: (r) => <StatusBadge status={r.status} /> };
const colNotes = { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} /> };
const colInitials = { key: "initials", label: "Entered By", render: (r) => <span className="font-mono-data uppercase">{r.initials || "—"}</span> };
const colCompletedAt = { key: "completed_at", label: "Completed", mono: true, render: (r) => r.completed_at ? fmtDate(r.completed_at.slice(0, 10)) : <span className="text-gray-300">—</span> };
const colAttach = { key: "attachments", label: "Files", render: (r) => <AttachmentsCell row={r} path="workorders" /> };
const colNextDue = { key: "next_due", label: "Next Due", render: (r) => (
  <div className="flex flex-col items-start">
    <DueBadge row={r} />
    <MarkDoneButton row={r} />
  </div>
) };

const TABS = [
  {
    key: "one_time", label: "Repairs & Projects", category: "one_time", filter: (r) => r.status !== "completed",
    fields: [fieldDate, fieldTitle, fieldBuilding, fieldContactStatus, fieldContactedWho, fieldContactedWhen, fieldStatus, fieldInitials, fieldNotes],
    columns: [colDate, colTask, colBuilding, colContacted, colStatus, colNotes, colInitials, colAttach],
  },
  {
    key: "recurring", label: "Regular Maintenance", category: "recurring",
    fields: [fieldDate, fieldTitle, fieldBuilding, { key: "repeat_months", label: "Repeat Every (months)", type: "number", required: true, placeholder: "3" }, { key: "last_done", label: "Last Completed (sets next reminder)", type: "date" }, fieldStatus, fieldInitials, fieldNotes],
    columns: [colDate, colTask, colBuilding, colStatus, colNextDue, colNotes, colInitials, colAttach],
  },
  {
    key: "completed", label: "Completed", category: "one_time", filter: (r) => r.status === "completed", hideAdd: true,
    fields: [fieldDate, fieldTitle, fieldBuilding, fieldStatus, fieldInitials, fieldNotes],
    columns: [colDate, colTask, colBuilding, colCompletedAt, colNotes, colInitials, colAttach],
  },
];

export default function BuildingMaint() {
  return (
    <div data-testid="facilities-section">
      <PageHeader title="Facilities" subtitle="Things that need done across the buildings and lot" />
      <DueThisMonth filterType="facilities" title="Facilities Due" />
      <Tabs defaultValue="one_time" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`facilities-tab-${t.key}`}
              className="rounded-none border-2 border-gray-900 px-4 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-0">
            <CrudSection
              title={t.label}
              subtitle=""
              path="workorders"
              category={t.category}
              filter={t.filter}
              hideAdd={t.hideAdd}
              testPrefix={`buildings-${t.key}`}
              addLabel="New Task"
              editLabel="Edit Task"
              fields={t.fields}
              columns={t.columns}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
